import { useState } from 'react';

import { useIsMutating } from '@tanstack/react-query';
import PropTypes from 'prop-types';
import { BsFunnel, BsSearch } from 'react-icons/bs';

import Button from '#globalComponents/Button/Button';
import Modal from '#globalComponents/Overlay/Modal';
import Panel from '#globalComponents/Overlay/Panel';

import { hasHrPreviewCriteria } from '../../../hooks/useSearchPreview';
import { prospectQueryMutationKeys } from '../../../models/prospectQueries/keys';

import { CollapseCard } from './CollapseCard';
import { RangeSliderWithInput } from './criteriaFormAtoms';
import { SearchCriteriaCards } from './SearchCriteriaCards';
import { SearchPreview } from './SearchPreview';

/**
 * Full-area "Suchkriterien" popup — the entry point for starting a lead search.
 *
 * Filters are collapsible cards in a grid; a preview rail on the right shows the live,
 * free Handelsregister match-count funnel.
 */
export const LeadCriteriaModal = ({
	open,
	onClose,
	filters,
	searchType,
	isGermany,
	hasActiveHRFilters,
	hasActiveMapsFilters,
	onFilterChange,
	onStartSearch,
	onSearchTypeToggle,
	previewIsLoading,
	previewIsResolving,
	previewData,
}) => {
	const searching = useIsMutating({ mutationKey: prospectQueryMutationKeys.unifiedSearch }) > 0;
	const [isLeadCountOpen, setIsLeadCountOpen] = useState(true);

	if (!open) return null;

	const isHR = searchType === 'hr';
	const canToggleToHR = !isHR && isGermany && !hasActiveMapsFilters;
	const canToggle = isHR || canToggleToHR;

	return (
		<Modal isOpen={open} onClose={onClose} size="xl">
			<Panel.Header icon={BsFunnel} subtitle="Definieren Sie Ihre neue Lead-Suche" onClose={onClose}>
				Suchkriterien
			</Panel.Header>

			{/* Each column scrolls on its own, so the body must clip rather than scroll as a whole.
			    The panel is only max-h constrained, so heights flow through flex + a 1fr grid row —
			    h-full percentages would fall back to content height and silently disable scrolling. */}
			<Panel.Body padded={false} scroll={false} className="bg-gray-50 flex flex-col">
				<div className="flex-1 min-h-0 grid grid-cols-12 grid-rows-[minmax(0,1fr)] gap-5 p-6">
					<div className="col-span-12 lg:col-span-8 min-h-0 overflow-y-auto grid grid-cols-2 gap-4 gap-x-4 content-start pr-1">
						<SearchCriteriaCards
							filters={filters}
							searchType={searchType}
							isGermany={isGermany}
							hasActiveHRFilters={hasActiveHRFilters}
							hasActiveMapsFilters={hasActiveMapsFilters}
							onFilterChange={onFilterChange}
						>
							<CollapseCard
								icon={BsFunnel}
								title="Leadanzahl"
								summary={`Max. ${filters.maxResults || 25}`}
								open={isLeadCountOpen}
								onToggle={() => setIsLeadCountOpen((o) => !o)}
							>
								<RangeSliderWithInput
									min={1}
									max={100}
									value={filters.maxResults || 25}
									onChange={(v) => onFilterChange('maxResults', v)}
									label="Max. Ergebnisse"
								/>
							</CollapseCard>
						</SearchCriteriaCards>
					</div>

					{/* Preview + actions rail */}
					<div className="col-span-12 lg:col-span-4 flex flex-col gap-4 lg:min-h-0 lg:overflow-y-auto">
						<SearchPreview
							searchType={searchType}
							hasCriteria={hasHrPreviewCriteria(filters)}
							isLoading={previewIsLoading}
							isResolving={previewIsResolving}
							data={previewData}
						/>

						<div className="bg-white rounded-xl border border-gray-200 shadow-sm p-4 mt-auto space-y-3">
							<button
								type="button"
								onClick={canToggle ? onSearchTypeToggle : undefined}
								disabled={!canToggle}
								title={
									canToggle
										? `Wechseln zu ${isHR ? 'Karten-Suche' : 'Handelsregister-Suche'}`
										: 'Handelsregister-Suche ist nur für Deutschland verfügbar'
								}
								className={`w-full text-xs text-center py-2 px-2 rounded-md transition-colors ${isHR ? 'bg-primary-100 text-primary-700' : 'bg-green-50 text-success-700'} ${canToggle ? 'cursor-pointer hover:ring-1 hover:ring-offset-1' : 'cursor-default opacity-75'}`}
							>
								{isHR ? 'Handelsregister-Suche (Northdata)' : 'Karten-Suche'}
							</button>
							<Button onClick={onStartSearch} loading={searching} variant="solid" size="md" fullWidth>
								<BsSearch className="mr-2" />
								Suche starten
							</Button>
							<Button onClick={() => onFilterChange('reset')} variant="outline" size="md" fullWidth>
								Alle Filter zurücksetzen
							</Button>
						</div>
					</div>
				</div>
			</Panel.Body>
		</Modal>
	);
};

LeadCriteriaModal.propTypes = {
	open: PropTypes.bool.isRequired,
	onClose: PropTypes.func.isRequired,
	filters: PropTypes.object.isRequired,
	searchType: PropTypes.oneOf(['maps', 'hr']).isRequired,
	isGermany: PropTypes.bool.isRequired,
	hasActiveHRFilters: PropTypes.bool.isRequired,
	hasActiveMapsFilters: PropTypes.bool.isRequired,
	onFilterChange: PropTypes.func.isRequired,
	onStartSearch: PropTypes.func.isRequired,
	onSearchTypeToggle: PropTypes.func.isRequired,
	previewIsLoading: PropTypes.bool,
	previewIsResolving: PropTypes.bool,
	previewData: PropTypes.object,
};

export default LeadCriteriaModal;
